import { FC, useCallback } from 'react'
import { toast } from 'sonner'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAPI } from '@/hooks/useAPI'
import { Admin, AdminRole } from '@/out/admin'
import { updateAdminRole } from '@/thunks/admins/role'
import { updateInvitationRole } from '@/thunks/invitations/role'

const roleTitles: Record<AdminRole, string> = {
  owner: 'Owner',
  admin: 'Administrator',
  supervisor: 'Supervisor',
}

type Props = {
  admin: Pick<Admin, 'id' | 'role'>
  thunk: typeof updateAdminRole | typeof updateInvitationRole
  onChange: (role: AdminRole) => void
}

const RoleSelect: FC<Props> = ({ admin, thunk, onChange }) => {
  const [update] = useAPI(thunk)

  const handleChange = useCallback(
    async (role: AdminRole) => {
      try {
        const [updated] = await update({
          id: admin.id,
          role,
        })
        onChange(updated.role)

        toast.success('Role updated successfully', {
          description: `Changed to ${roleTitles[role]}`,
        })
      } catch (error: unknown) {
        console.error(error)
        toast.error('Failed to update role', {
          description: 'Please try again later',
        })
      }
    },
    [admin, onChange, update]
  )

  return (
    <Select value={admin.role} onValueChange={handleChange}>
      <SelectTrigger className="h-8 w-36" data-testid="admin-role-trigger">
        <SelectValue data-testid="admin-role-value" />
      </SelectTrigger>
      <SelectContent data-testid="admin-role-select-content">
        <SelectItem data-testid="admin-role-admin" value="admin" className="cursor-pointer">
          Administrator
        </SelectItem>
        <SelectItem data-testid="admin-role-supervisor" value="supervisor" className="cursor-pointer">
          Supervisor
        </SelectItem>
      </SelectContent>
    </Select>
  )
}

export default RoleSelect
